import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Bot, Database, Loader2, RotateCcw, Send, User } from 'lucide-react'
import Layout from '../../components/Layout'
import { marketChatService } from '../../services/marketChatService'

const suggestions = [
  'Kolik bytů se prodalo v Brně v roce 2026 a jak je to oproti roku 2025?',
  'Jaká je průměrná a mediánová doba prodeje bytů 3+1 v Kohoutovicích?',
  'Kolik je právě aktivních nabídek bytů v Jundrově?',
  'Která městská část Brna má nejvíce aktivních nabídek bytů?',
  'Najdi v Žabovřeskách aktivní byt s největším poklesem ceny.',
]

const Message = ({ message }) => {
  const isUser = message.role === 'user'
  return (
    <div className={`flex gap-3 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <div className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
          <Bot className="h-4 w-4" />
        </div>
      )}
      <div
        className={`max-w-3xl rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm ${
          isUser ? 'bg-purple-600 text-white' : 'border border-gray-200 bg-white text-gray-800'
        }`}
      >
        {isUser ? <p className="whitespace-pre-wrap">{message.content}</p> : <ReactMarkdown className="market-ai-markdown">{message.content}</ReactMarkdown>}
        {!isUser && message.toolsUsed?.length > 0 && (
          <div className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-400">Ověřeno z dat Jestřába</div>
        )}
        {!isUser && message.debug?.length > 0 && (
          <details className="mt-3 border-t border-gray-200 pt-2 text-xs text-gray-600">
            <summary className="cursor-pointer select-none font-medium text-purple-700">Debug query</summary>
            <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-gray-950 p-3 text-left font-mono text-[11px] leading-5 text-green-300">
              {JSON.stringify(message.debug, null, 2)}
            </pre>
          </details>
        )}
      </div>
      {isUser && (
        <div className="mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-600">
          <User className="h-4 w-4" />
        </div>
      )}
    </div>
  )
}

const MarketAI = () => {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [debugEnabled, setDebugEnabled] = useState(false)
  const endRef = useRef(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const submit = async (event, suggestedText) => {
    event?.preventDefault()
    const content = (suggestedText || input).trim()
    if (!content || loading) return

    const nextMessages = [...messages, { role: 'user', content }]
    setMessages(nextMessages)
    setInput('')
    setError('')
    setLoading(true)

    try {
      const response = await marketChatService.send(
        nextMessages.map(({ role, content: text }) => ({ role, content: text })),
        debugEnabled
      )
      setMessages((current) => [
        ...current,
        { role: 'assistant', content: response.message, toolsUsed: response.toolsUsed || [], debug: response.debug || [] },
      ])
    } catch (requestError) {
      setError(requestError.message || 'Market AI se nepodařilo načíst.')
    } finally {
      setLoading(false)
    }
  }

  const headerContent = (
    <div className="flex w-full items-center justify-between gap-4">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">Market AI</h1>
        <p className="text-xs text-gray-500">Analýza nabídek z dat Realitního Jestřába</p>
      </div>
      <div className="flex items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={debugEnabled}
            onChange={(event) => setDebugEnabled(event.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Debug
        </label>
        <button
          type="button"
          onClick={() => {
            setMessages([])
            setError('')
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          <RotateCcw className="h-4 w-4" />
          Nový chat
        </button>
      </div>
    </div>
  )

  return (
    <Layout headerContent={headerContent}>
      <div className="mr-auto flex h-full w-full max-w-5xl flex-col px-4 py-6 sm:px-6">
        <div className="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-gray-200 bg-gray-50 p-4 sm:p-6">
          {messages.length === 0 ? (
            <div className="mx-auto flex max-w-3xl flex-col items-center py-8 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-100 text-purple-700">
                <Database className="h-7 w-7" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Na co se chcete zeptat?</h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-gray-500">
                Market AI umí počty nabídek, meziroční srovnání, nabídkové ceny, cenu za m², dobu na trhu a žebříčky inzerátů.
              </p>
              <div className="mt-6 grid w-full gap-2 sm:grid-cols-2">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => submit(null, suggestion)}
                    className="rounded-xl border border-gray-200 bg-white p-3 text-left text-sm leading-5 text-gray-700 shadow-sm transition hover:border-purple-300 hover:bg-purple-50"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {messages.map((message, index) => (
                <Message key={`${message.role}-${index}`} message={message} />
              ))}
              {loading && (
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 text-purple-700">
                    <Bot className="h-4 w-4" />
                  </div>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Ověřuji data…
                </div>
              )}
              <div ref={endRef} />
            </div>
          )}
        </div>

        {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        <form onSubmit={submit} className="mt-4 flex gap-2">
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) submit(event)
            }}
            rows={2}
            maxLength={4000}
            placeholder="Např. Porovnej nabídkové ceny bytů 3+1 v Kohoutovicích za roky 2025 a 2026…"
            disabled={loading}
            className="min-h-[52px] flex-1 resize-none rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none ring-purple-500 placeholder:text-gray-400 focus:ring-2 disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="inline-flex h-[52px] items-center justify-center gap-2 rounded-xl bg-purple-600 px-5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            <Send className="h-4 w-4" />
            Odeslat
          </button>
        </form>
      </div>
    </Layout>
  )
}

export default MarketAI
